/**
 * Haber akışı sözleşmeleri (M3 WP 3.10). Sistem haberleri domain event'lerden üretilir (migration 0040); metin istemcide olay
 * tipinden Türkçe cümleye çevrilir (sunucu yalnız yapısal veri döner). Kayda bağlı haber/not yalnız kaydı okuyabilene görünür;
 * kayda bağlı olmayan not tüm çalışma alanına açıktır. Yükte tutar veya alan değeri yoktur.
 *
 * İkinci dilim (migration 0052; K-M3-11): not etiketleri (7 sistem etiketi + tenant etiketi, M2 lookup deseni), müşteri
 * teması + duygu, haber tipleri ve süzgeç paneli, not eki / sesli not (M2 özel dosya hattı), "Gündemdekiler" ve sağ panel.
 */
import { z } from 'zod';
const uuid = z.guid();
export const FEED_SYSTEM_EVENTS = [
    'record.created',
    'lead.status_changed',
    'lead.converted',
    'opportunity.status_changed',
    'opportunity.reopened',
    'opportunity.stage_changed',
    'proposal.approval_requested',
    'proposal.approved',
    'proposal.approval_rejected',
    'proposal.finalized',
    'proposal.revised',
    'proposal.status_changed',
    'proposal.converted_to_order',
    'order.status_changed',
    'order.split',
    'purchase_order.status_changed',
    'contract.status_changed',
];
export const FEED_FILTERS = ['all', 'notes', 'system'];
export const FEED_PAGE_MAX = 30;
export const FEED_NOTE_MAX = 5000;
export const FEED_COMMENT_MAX = 2000;
// ---------------------------------------------------------------------------------------------------------------------
// Not etiketleri (O02/O10: "7 not etiketi + yeni"). Keşifte etiket SAYISI kayıtlı, ADLARI kayıtlı değil → adlar Castinflow
// ürün kararıdır (K-M3-11). Desen M2 lookup'larıyla aynı: sistem etiketi koddadır (silinemez; ad/sıra/aktiflik tenant
// satırıyla ezilebilir), tenant etiketi `c_…` anahtarıyla eklenir; silme yok, pasifleştirme var.
// ---------------------------------------------------------------------------------------------------------------------
export const FEED_NOTE_TAG_KEY_RE = /^[a-z][a-z0-9_]{1,39}$/;
export const FEED_NOTE_TAG_LABEL_MAX = 40;
/** Tenant başına en fazla özel not etiketi */
export const FEED_CUSTOM_NOTE_TAGS_MAX = 50;
/** Bir notta en fazla etiket */
export const FEED_NOTE_TAGS_PER_NOTE_MAX = 10;
export const FEED_SYSTEM_NOTE_TAGS = [
    { key: 'meeting', label: 'Toplantı' },
    { key: 'phone_call', label: 'Telefon görüşmesi' },
    { key: 'visit', label: 'Müşteri ziyareti' },
    { key: 'email', label: 'E-posta yazışması' },
    { key: 'offer', label: 'Fiyat / teklif görüşmesi' },
    { key: 'complaint', label: 'Şikâyet' },
    { key: 'info', label: 'Bilgilendirme' },
];
export const feedNoteTagSchema = z.object({
    key: z.string().regex(FEED_NOTE_TAG_KEY_RE),
    label: z.string(),
    isActive: z.boolean(),
    isSystem: z.boolean(),
    sortOrder: z.number().int(),
});
export const feedNoteTagListSchema = z.object({ data: z.array(feedNoteTagSchema) });
const tagLabel = z.string().trim().min(1, 'etiket adı boş olamaz').max(FEED_NOTE_TAG_LABEL_MAX);
export const createFeedNoteTagInputSchema = z
    .object({ label: tagLabel, sortOrder: z.number().int().min(0).max(10_000).optional() })
    .strict();
export const updateFeedNoteTagInputSchema = z
    .object({
    label: tagLabel.optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Değişiklik yok' });
/** Sistem etiketleri + tenant satırları → birleşik liste (sıra: sortOrder, sonra ad). */
export function mergeFeedNoteTags(rows) {
    const out = new Map();
    FEED_SYSTEM_NOTE_TAGS.forEach((s, i) => out.set(s.key, { ...s, isActive: true, isSystem: true, sortOrder: i * 10 }));
    for (const r of rows) {
        const sys = out.get(r.key);
        if (sys?.isSystem)
            out.set(r.key, {
                ...sys,
                label: r.label ?? sys.label,
                isActive: r.isActive,
                sortOrder: r.sortOrder,
            });
        else
            out.set(r.key, {
                key: r.key,
                label: r.label ?? r.key,
                isActive: r.isActive,
                isSystem: false,
                sortOrder: r.sortOrder,
            });
    }
    return [...out.values()].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'tr'));
}
// ---------------------------------------------------------------------------------------------------------------------
// Müşteri teması + duygu (O02 gizli alanlar `IsCustomerContacted` / `CustomerContactStatus`; O04/O05 süzgeç)
// ---------------------------------------------------------------------------------------------------------------------
export const FEED_CONTACT_SENTIMENTS = ['positive', 'neutral', 'negative'];
export const FEED_CONTACT_SENTIMENT_LABELS = {
    positive: 'Olumlu',
    neutral: 'Nötr',
    negative: 'Olumsuz',
};
// ---------------------------------------------------------------------------------------------------------------------
// Haber tipleri (O04 "Not tipi", `nTypes` 18). Keşifte 18 tip SAYISI kayıtlı, adları kayıtlı değil. Castinflow yalnız
// GERÇEKTEN ürettiği tipleri sunar: kullanıcı notu, sesli not ve M3 modüllerinin kayıt gelişmeleri. Aktivite, destek
// talebi, e-posta, web formu vb. tipler ilgili M4+ modülü haber üretince eklenir (K-M3-11) — sahte tip yok.
// ---------------------------------------------------------------------------------------------------------------------
export const FEED_ITEM_TYPES = [
    'note',
    'voice_note',
    'lead',
    'opportunity',
    'proposal',
    'order',
    'purchase_order',
    'contract',
    'company',
    'contact',
    // M4 (migration 0057): aktivite oluşturma/tamamlama haberleri
    'activity',
    // M5 (migration 0063): form belgesi haberleri (formun yayın ayarı açıksa)
    'form_document',
];
export const FEED_ITEM_TYPE_LABELS = {
    note: 'Notlar',
    voice_note: 'Sesli notlar',
    lead: 'Müşteri adayı gelişmeleri',
    opportunity: 'Fırsat gelişmeleri',
    proposal: 'Teklif gelişmeleri',
    order: 'Sipariş gelişmeleri',
    purchase_order: 'Tedarikçi siparişi gelişmeleri',
    contract: 'Sözleşme gelişmeleri',
    company: 'Yeni şirketler',
    contact: 'Yeni kişiler',
    activity: 'Aktiviteler',
    form_document: 'Form belgeleri',
};
/** Kayıt gelişmesi tipleri (sistem haberinin kayıt türü) */
export const FEED_RECORD_ITEM_TYPES = FEED_ITEM_TYPES.filter((t) => t !== 'note' && t !== 'voice_note');
export function feedItemTypeOf(i) {
    if (i.kind === 'note')
        return i.hasVoice ? 'voice_note' : 'note';
    return FEED_ITEM_TYPES.includes(i.recordType ?? '')
        ? i.recordType
        : null;
}
// ---------------------------------------------------------------------------------------------------------------------
// Not eki ve sesli not: M2 özel dosya hattı (`attachments`; kayda bağlı, tarama `clean` olmadan indirilemez). Bu yüzden
// ek/ses yalnız KAYDA BAĞLI notta olur ve kayda yazma yetkisi ister (M2 yükleme kuralı).
// ---------------------------------------------------------------------------------------------------------------------
export const FEED_NOTE_FILES_MAX = 5;
/** Sesli not üst sınırı: 5 dakika (kayıt istemcide bu sürede durur) */
export const FEED_VOICE_MAX_SECONDS = 300;
/** Sesli not boyut üst sınırı (sunucu da uygular; 5 dk opus ≈ 1–2 MB) */
export const FEED_VOICE_MAX_BYTES = 10 * 1024 * 1024;
export const FEED_ATTACHMENT_STATES = ['scanning', 'ready', 'blocked', 'removed'];
const optionalBody = z.string().trim().max(FEED_NOTE_MAX).optional();
export const createNoteInputSchema = z
    .object({
    body: optionalBody,
    /** Kayda bağlı not (kaydı okuyabilen herkese görünür); verilmezse çalışma alanı geneli */
    recordId: uuid.nullable().optional(),
    /** Not etiketi anahtarları (etkin etiketler) */
    tags: z
        .array(z.string().regex(FEED_NOTE_TAG_KEY_RE))
        .max(FEED_NOTE_TAGS_PER_NOTE_MAX)
        .optional(),
    /** "Müşteriyle temas sağlandı mı?" — true/false; verilmezse belirtilmedi */
    contacted: z.boolean().nullable().optional(),
    /** Temasın gidişatı; yalnız `contacted: true` iken */
    sentiment: z.enum(FEED_CONTACT_SENTIMENTS).nullable().optional(),
    /** Yüklenmiş (finalize edilmiş) ek kimlikleri; aynı kayda ve bu kullanıcıya ait olmalı */
    attachmentIds: z.array(uuid).max(FEED_NOTE_FILES_MAX).optional(),
    /** Sesli not: yüklenmiş ses eki + süre (ms) */
    voice: z
        .object({
        attachmentId: uuid,
        durationMs: z
            .number()
            .int()
            .min(1)
            .max(FEED_VOICE_MAX_SECONDS * 1000),
    })
        .strict()
        .nullable()
        .optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    const files = v.attachmentIds ?? [];
    const hasMedia = files.length > 0 || (v.voice ?? null) !== null;
    if ((v.body ?? '') === '' && !hasMedia)
        ctx.addIssue({ code: 'custom', path: ['body'], message: 'not boş olamaz' });
    if (new Set(v.tags ?? []).size !== (v.tags ?? []).length)
        ctx.addIssue({ code: 'custom', path: ['tags'], message: 'aynı etiket iki kez verilemez' });
    if (new Set(files).size !== files.length || (v.voice && files.includes(v.voice.attachmentId)))
        ctx.addIssue({
            code: 'custom',
            path: ['attachmentIds'],
            message: 'aynı dosya iki kez verilemez',
        });
    if ((v.sentiment ?? null) !== null && v.contacted !== true)
        ctx.addIssue({
            code: 'custom',
            path: ['sentiment'],
            message: 'gidişat yalnız müşteriyle temas sağlandıysa seçilir',
        });
    const bound = (v.recordId ?? null) !== null;
    if (!bound && (v.contacted ?? null) !== null)
        ctx.addIssue({
            code: 'custom',
            path: ['contacted'],
            message: 'müşteri teması yalnız bir kayda bağlı notta işaretlenir',
        });
    if (!bound && hasMedia)
        ctx.addIssue({
            code: 'custom',
            path: ['recordId'],
            message: 'dosya veya sesli not eklemek için notu bir kayda bağlayın',
        });
});
export const createCommentInputSchema = z
    .object({ body: z.string().trim().min(1, 'yorum boş olamaz').max(FEED_COMMENT_MAX) })
    .strict();
export const feedCommentSchema = z.object({
    id: uuid,
    authorName: z.string().nullable(),
    mine: z.boolean(),
    body: z.string(),
    createdAt: z.string(),
});
export const feedAttachmentSchema = z.object({
    id: uuid,
    role: z.enum(['file', 'voice']),
    fileName: z.string(),
    contentType: z.string().nullable(),
    size: z.number().int(),
    /** Yalnız sesli notta */
    durationMs: z.number().int().nullable(),
    /** scanning: tarama sürüyor · ready: indirilebilir · blocked: tarama reddetti/başarısız · removed: silindi */
    state: z.enum(FEED_ATTACHMENT_STATES),
});
export const feedItemSchema = z.object({
    id: uuid,
    kind: z.enum(['system', 'note']),
    /** Süzgeç tipi (bilinmeyen kayıt türünün sistem haberinde null) */
    itemType: z.enum(FEED_ITEM_TYPES).nullable(),
    eventType: z.string().nullable(),
    /** Kayda bağlıysa ve çağıran okuyabiliyorsa */
    record: z.object({ type: z.string(), id: uuid, title: z.string() }).nullable(),
    actorName: z.string().nullable(),
    mine: z.boolean(),
    /** Güvenli yük: durum geçişi, revizyon no, bağlı kayıt kimliği */
    data: z.record(z.string(), z.unknown()),
    body: z.string().nullable(),
    createdAt: z.string(),
    likeCount: z.number().int(),
    likedByMe: z.boolean(),
    commentCount: z.number().int(),
    /** Son yorumlar (en çok 3; eskiden yeniye) */
    comments: z.array(feedCommentSchema),
    /** Not etiketleri (pasif etiket adıyla görünmeye devam eder) */
    tags: z.array(z.object({ key: z.string(), label: z.string() })),
    contacted: z.boolean().nullable(),
    sentiment: z.enum(FEED_CONTACT_SENTIMENTS).nullable(),
    attachments: z.array(feedAttachmentSchema),
});
export const feedListSchema = z.object({
    data: z.array(feedItemSchema),
    nextCursor: z.string().nullable(),
});
export const EMPTY_FEED_FILTERS = {
    types: [],
    authorId: null,
    from: null,
    to: null,
    tags: [],
    contacted: false,
    sentiments: [],
    unlinked: false,
    withAttachments: false,
};
/** Etkin süzgeç sayısı (düğme rozetinde) */
export function activeFeedFilterCount(f) {
    return ((f.types.length > 0 ? 1 : 0) +
        (f.authorId ? 1 : 0) +
        (f.from || f.to ? 1 : 0) +
        (f.tags.length > 0 ? 1 : 0) +
        (f.contacted || f.sentiments.length > 0 ? 1 : 0) +
        (f.unlinked ? 1 : 0) +
        (f.withAttachments ? 1 : 0));
}
/** Süzgeç → sorgu parametreleri (boş olanlar yazılmaz) */
export function feedFilterParams(f) {
    const out = {};
    if (f.types.length > 0)
        out['types'] = f.types.join(',');
    if (f.authorId)
        out['author'] = f.authorId;
    if (f.from)
        out['from'] = f.from;
    if (f.to)
        out['to'] = f.to;
    if (f.tags.length > 0)
        out['tags'] = f.tags.join(',');
    if (f.contacted)
        out['contacted'] = 'true';
    if (f.sentiments.length > 0)
        out['sentiment'] = f.sentiments.join(',');
    if (f.unlinked)
        out['unlinked'] = 'true';
    if (f.withAttachments)
        out['attachments'] = 'true';
    return out;
}
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const isoDay = z
    .string()
    .regex(DATE_RE, 'tarih YYYY-AA-GG olmalı')
    .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), 'geçersiz tarih');
const csv = (item, max) => z
    .string()
    .max(1000)
    .transform((s) => s.split(',').filter((x) => x !== ''))
    .pipe(z.array(item).max(max));
const flag = z.enum(['true', 'false']).transform((v) => v === 'true');
/** `GET /feed` sorgusu (API tarafı ayrıştırma; web `feedFilterParams` ile üretir) */
export const feedListQuerySchema = z
    .object({
    filter: z.enum(FEED_FILTERS).default('all'),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(FEED_PAGE_MAX).default(20),
    recordId: uuid.optional(),
    types: csv(z.enum(FEED_ITEM_TYPES), FEED_ITEM_TYPES.length).optional(),
    author: uuid.optional(),
    from: isoDay.optional(),
    to: isoDay.optional(),
    tags: csv(z.string().regex(FEED_NOTE_TAG_KEY_RE), 20).optional(),
    contacted: flag.optional(),
    sentiment: csv(z.enum(FEED_CONTACT_SENTIMENTS), 3).optional(),
    unlinked: flag.optional(),
    attachments: flag.optional(),
})
    .strict()
    .refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: 'başlangıç tarihi bitişten sonra olamaz',
    path: ['from'],
});
export function feedFiltersFromQuery(q) {
    return {
        types: q.types ?? [],
        authorId: q.author ?? null,
        from: q.from ?? null,
        to: q.to ?? null,
        tags: q.tags ?? [],
        contacted: q.contacted ?? false,
        sentiments: q.sentiment ?? [],
        unlinked: q.unlinked ?? false,
        withAttachments: q.attachments ?? false,
    };
}
// ---------------------------------------------------------------------------------------------------------------------
// Sağ panel (O01/O03) ve "Gündemdekiler" (ÜK, K-M3-11)
// ---------------------------------------------------------------------------------------------------------------------
/** Gündemdekiler: son 7 günde görebildiğin kayıtlar arasında en çok haber + yorum + beğeni alan 5 kayıt; eşitlikte en yeni */
export const FEED_TRENDING_DAYS = 7;
export const FEED_TRENDING_LIMIT = 5;
/** Sıralama öncesi değerlendirilen aday kayıt sayısı (ACL süzgecinden önce; tarama sınırı) */
export const FEED_TRENDING_SCAN = 100;
const recordRef = z.object({ type: z.string(), id: uuid, title: z.string() });
export const feedTrendingSchema = z.object({
    windowDays: z.number().int(),
    data: z.array(z.object({
        record: recordRef,
        score: z.number().int(),
        items: z.number().int(),
        comments: z.number().int(),
        likes: z.number().int(),
        lastActivityAt: z.string(),
    })),
});
/** Sağ panelin "aktif" kayıt bölümleri (M3 verisiyle desteklenenler) */
export const FEED_PANEL_TYPES = ['opportunity', 'company', 'contact', 'product'];
export const FEED_PANEL_LIMIT = 5;
export const feedPanelSchema = z.object({
    active: z.object({
        opportunity: z.array(recordRef),
        company: z.array(recordRef),
        contact: z.array(recordRef),
        product: z.array(recordRef),
    }),
});
// ---------------------------------------------------------------------------------------------------------------------
// Canlı ekip akışı (TI-012, ADR-0019): sayfa açıkken yeni içerik. Canlı kanal (SSE `GET /live`) yalnız İPUCU taşır (öğe
// kimlikleri); içerik istemcinin kendi süzgeciyle `GET /feed/since` ucundan okunur → "N yeni gönderi" bandındaki sayı, banda
// tıklanınca eklenen listenin aynısıdır (aynı süzgeç + aynı ACL). Kopma sonrası telafi aynı uçtan imleçle yapılır; istemci
// kimlikle tekilleştirir. Geç işlenen (eski zaman damgalı) satırlar için sunucu imleçten `FEED_SINCE_OVERLAP_SECONDS`
// geriye bakar; daha önce görülen kimlikler istemcide elenir.
// ---------------------------------------------------------------------------------------------------------------------
/** "Yeni" sorgusunun en çok dönen öğe sayısı; aşılırsa `more: true` (bant "30+ yeni" der, tıklanınca liste baştan yüklenir) */
export const FEED_SINCE_MAX = 30;
/** İmleçten geriye bakma penceresi (geç işlenen işlemler); istemci kimlikle tekilleştirir */
export const FEED_SINCE_OVERLAP_SECONDS = 60;
/** Tek öğenin tüm yorumları (ayrıntı) için üst sınır */
export const FEED_COMMENTS_MAX = 500;
export const feedSinceParamsSchema = z
    .object({
    sinceAt: z.iso.datetime({ offset: true }),
    sinceId: uuid,
})
    .strict();
export const feedSinceSchema = z.object({
    /** En yeniden eskiye; imleçten (geriye bakma penceresi dahil) yeni ve görünür öğeler */
    data: z.array(feedItemSchema),
    /** Sınır aşıldı: daha fazla yeni öğe var */
    more: z.boolean(),
});
export const feedCommentListSchema = z.object({
    data: z.array(feedCommentSchema),
    /** Toplam yorum sayısı (sınır aşıldıysa `data` daha kısa) */
    total: z.number().int(),
});
// ---------------------------------------------------------------------------------------------------------------------
// Canlı kanal olayları (SSE). Yük yalnız kimlik/işaret taşır; içerik ve sayılar her zaman yetkili uçlardan okunur.
// ---------------------------------------------------------------------------------------------------------------------
export const LIVE_EVENT_TYPES = [
    'ready',
    'feed',
    'feed-update',
    'notification',
    'conversation',
    'reauth',
];
export const liveFeedHintSchema = z.object({
    /** Çağırana görünür yeni öğelerin kimlikleri (başkasının yazdıkları; kendi yazdıkları `mine` ile işaretli) */
    ids: z.array(uuid),
    mine: z.array(uuid).default([]),
});
/**
 * G1 konuşma olayı türleri (ADR-0019 deseni): yeni ileti (`message`), sorumlu değişti (`assignment`), konuşmanın başka bir alanı
 * değişti (durum, öncelik, bağ — `updated`), konuşmayı kimin yürüttüğü değişti (devralma, asistana devir, devir isteği — `control`),
 * çağıranın kendi okunma imleci ilerledi (başka sekme/cihaz — `read`). Yük yalnız konuşma kimliği + tür; içerik ve sayılar yetkili
 * uçlardan okunur. Tür ipucudur: istemci her türde aynı sorguları tazeler.
 */
export const LIVE_CONVERSATION_KINDS = [
    'message',
    'assignment',
    'updated',
    'control',
    'read',
];
export const liveConversationHintSchema = z.object({
    items: z
        .array(z.object({ id: uuid, kind: z.enum(LIVE_CONVERSATION_KINDS) }))
        .min(1)
        .max(500),
});
/** Kalp atışı aralığı (kenar katmanı boşta bağlantıyı ~100 sn'de keser; altında kalınır) */
export const LIVE_HEARTBEAT_SECONDS = 20;
/** Canlı kanal kullanılamazsa yedek yoklama aralığı */
export const LIVE_FALLBACK_POLL_SECONDS = 20;
//# sourceMappingURL=feed.js.map